import React, { useState } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';

const NoticesContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 2rem;
`;

const NoticesList = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const NoticeCard = styled.div`
  padding: 1.5rem;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #000000;
    background: #f8f9fa;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const NoticeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 1rem;
`;

const NoticeTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: #000000;
  flex: 1;
`;

const NoticeDate = styled.div`
  font-size: 0.8125rem;
  color: #6c757d;
  font-weight: 600;
  white-space: nowrap;
`;

const NoticeContent = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  line-height: 1.6;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #adb5bd;
  font-size: 1rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ViewModalContent = styled.div`
  margin-bottom: 1rem;
`;

const ViewTitle = styled.h3`
  font-size: 1.375rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1rem;
  line-height: 1.4;
`;

const ViewDate = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #dee2e6;
`;

const ViewContent = styled.div`
  font-size: 0.9375rem;
  color: #495057;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
`;

interface Notice {
  id: number;
  title: string;
  content: string;
  createdAt: string;
  author: string;
}

const MemberNotices: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);

  // TODO: 백엔드 API에서 공지사항 목록 가져오기
  // GET /api/notices
  const [notices] = useState<Notice[]>([
    {
      id: 1,
      title: '1월 정기 총회 안내',
      content: '안녕하세요.\n\n1월 정기 총회를 다음과 같이 개최합니다.\n\n일시: 2025년 1월 20일 (토) 오후 7시\n장소: 수원체육관\n\n참석 부탁드립니다.',
      createdAt: '2025-01-05',
      author: '관리자'
    },
    {
      id: 2,
      title: '회비 납부 안내',
      content: '1월 회비 납부 기한은 1월 10일까지입니다.\n\n계좌번호: 0000000000\n입금자명: 본인 이름\n\n기한 내 납부 부탁드립니다.',
      createdAt: '2025-01-03',
      author: '관리자'
    },
    {
      id: 3,
      title: '유니폼 신청 마감 안내',
      content: '2025년 새 유니폼 신청이 1월 15일에 마감됩니다.\n\n아직 신청하지 않으신 분들은 서둘러 주세요.',
      createdAt: '2025-01-08',
      author: '관리자'
    },
  ]);

  const handleNoticeClick = (notice: Notice) => {
    setSelectedNotice(notice);
    setIsModalOpen(true);
  };

  return (
    <DashboardLayout title="공지사항">
      <NoticesContainer>
        <Title>공지사항</Title>
        
        <NoticesList>
          {notices.length > 0 ? (
            notices.map(notice => (
              <NoticeCard key={notice.id} onClick={() => handleNoticeClick(notice)}>
                <NoticeHeader>
                  <NoticeTitle>{notice.title}</NoticeTitle>
                  <NoticeDate>{notice.createdAt}</NoticeDate>
                </NoticeHeader>
                <NoticeContent>{notice.content}</NoticeContent>
              </NoticeCard>
            ))
          ) : (
            <EmptyState>등록된 공지사항이 없습니다.</EmptyState>
          )}
        </NoticesList>
        
        {/* 공지사항 상세 보기 모달 */}
        <ModalOverlay 
          isOpen={isModalOpen}
          onClick={() => setIsModalOpen(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>공지사항</ModalTitle>
              <CloseButton onClick={() => setIsModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            
            {selectedNotice && (
              <ViewModalContent>
                <ViewTitle>{selectedNotice.title}</ViewTitle>
                <ViewDate>
                  작성일: {selectedNotice.createdAt} | 작성자: {selectedNotice.author}
                </ViewDate>
                <ViewContent>{selectedNotice.content}</ViewContent>
              </ViewModalContent>
            )}
          </ModalContent>
        </ModalOverlay>
      </NoticesContainer>
    </DashboardLayout>
  );
};

export default MemberNotices;

